<template>
  <Teleport to="body">
    <Transition name="ss-fade">
      <div v-if="modelValue" class="ss-overlay" @click.self="close">
        <div class="ss-box" role="dialog" aria-modal="true" :aria-label="locale === 'en' ? 'Search' : 'Arama'">

          <!-- Input -->
          <div class="ss-input-row">
            <svg class="ss-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              ref="inputRef"
              v-model="query"
              class="ss-input"
              type="search"
              autocomplete="off"
              :placeholder="locale === 'en' ? 'Search rooms, restaurants, spa, halls…' : 'Oda, restoran, spa, salon ara…'"
              :aria-label="locale === 'en' ? 'Search' : 'Ara'"
              @keydown.esc="close"
              @keydown.enter="goFirst"
            />
            <button class="ss-close" @click="close" :aria-label="locale === 'en' ? 'Close' : 'Kapat'">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <!-- Results -->
          <div class="ss-results" ref="resultsRef">

            <!-- Default suggestions -->
            <template v-if="!query.trim()">
              <p class="ss-label">{{ locale === 'en' ? 'Suggestions' : 'Öneriler' }}</p>
              <NuxtLink
                v-for="item in defaultItems"
                :key="item.path"
                :to="item.path"
                class="ss-result"
                @click="close"
              >
                <span class="ss-rlabel">{{ item.label }}</span>
              </NuxtLink>
            </template>

            <!-- Filtered -->
            <template v-else-if="hits.length">
              <NuxtLink
                v-for="item in hits"
                :key="item.path"
                :to="item.path"
                class="ss-result"
                @click="close"
              >
                <span class="ss-rlabel">{{ item.label }}</span>
                <span v-if="item.cat !== 'page'" class="ss-rcat">{{ catLabel(item.cat) }}</span>
              </NuxtLink>
            </template>

            <!-- No results -->
            <template v-else>
              <p class="ss-empty">{{ locale === 'en' ? 'No results found.' : 'Sonuç bulunamadı.' }}</p>
            </template>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick } from 'vue'

const props = defineProps({ modelValue: { type: Boolean, default: false } })
const emit  = defineEmits(['update:modelValue'])

const { locale } = useI18n()
const query    = ref('')
const inputRef = ref(null)
const resultsRef = ref(null)

function close() {
  emit('update:modelValue', false)
  query.value = ''
}

watch(() => props.modelValue, async (val) => {
  if (val) { await nextTick(); inputRef.value?.focus() }
  else { query.value = '' }
})

// ── Bilingual data — each item carries both TR/EN labels + keyword aliases ───
// _search is built at compute-time as a single lowercase string to match against.
// This means searching "deniz" finds Seafront Room even in EN mode, and "sea"
// finds it in TR mode.

const RAW = [
  // ── Pages ────────────────────────────────────────────────────────────────
  { tr: 'Ana Sayfa',           en: 'Home',                path: '/',                cat: 'page',     kw: ['home','ana','sayfa'] },
  { tr: 'Odalar',              en: 'Rooms',               path: '/rooms',            cat: 'page',     kw: ['oda','rooms','konaklama','accommodation','yatak'] },
  { tr: 'Restoranlar & Barlar',en: 'Restaurants & Bars',  path: '/flavors',          cat: 'page',     kw: ['restoran','bar','yemek','flavors','food','içki','dining'] },
  { tr: 'Lemon Spa & Wellness',en: 'Lemon Spa & Wellness',path: '/spa',              cat: 'page',     kw: ['spa','wellness','lemon','hamam','sauna','masaj','massage','terapi','therapy'] },
  { tr: 'Etkinlikler',         en: 'Events',              path: '/events',           cat: 'page',     kw: ['etkinlik','event','toplantı','meeting','konferans','conference','düğün','wedding','kongre','congress'] },
  { tr: 'Yaz Konsepti',        en: 'Summer Concept',      path: '/concepts/summer',  cat: 'page',     kw: ['yaz','summer','konsept','concept','her şey dahil','all inclusive','ultra her şey dahil','ultra all inclusive'] },
  { tr: 'Kış Konsepti',        en: 'Winter Concept',      path: '/concepts/winter',  cat: 'page',     kw: ['kış','winter','konsept','concept'] },
  { tr: 'Galeri',              en: 'Gallery',             path: '/gallery',          cat: 'page',     kw: ['galeri','gallery','fotoğraf','photo','resim','image'] },
  { tr: 'Kıbrıs Rehberi',      en: 'Cyprus Guide',        path: '/blog',             cat: 'page',     kw: ['kıbrıs','cyprus','rehber','guide','blog','gezi','travel'] },
  { tr: 'İletişim',            en: 'Contact',             path: '/contact',          cat: 'page',     kw: ['iletişim','contact','telefon','phone','email','adres','address','ulaşım','directions'] },
  { tr: 'Sık Sorulan Sorular', en: 'FAQ',                 path: '/faqs',             cat: 'page',     kw: ['sss','faq','soru','question','yardım','help'] },
  { tr: 'Hakkımızda',          en: 'About',               path: '/about',            cat: 'page',     kw: ['hakkımızda','about','tarih','history','biz','us'] },
  { tr: 'Kampanyalar',         en: 'Campaigns',           path: '/campaigns',        cat: 'page',     kw: ['kampanya','campaign','indirim','discount','teklif','offer','fırsat','deal'] },
  { tr: 'Kalite',              en: 'Quality',             path: '/quality',          cat: 'page',     kw: ['kalite','quality','sertifika','certificate','iso'] },

  // ── Rooms ────────────────────────────────────────────────────────────────
  { tr: 'Standart Deniz Manzaralı Oda', en: 'Standard Sea View Room',
    path: '/rooms/standart-sea-view-room', cat: 'room',
    kw: ['standart','standard','deniz','sea','manzara','view','oda','room','293','ana bina','main building'] },

  { tr: 'Deniz Cepheli Oda',   en: 'Seafront Room',
    path: '/rooms/seafront-room', cat: 'room',
    kw: ['deniz','sea','cephe','front','seafront','oda','room','plaj','beach'] },

  { tr: 'Park Standart Oda',   en: 'Park Standard Room',
    path: '/rooms/park-standart-room', cat: 'room',
    kw: ['park','standart','standard','oda','room','salamis park'] },

  { tr: 'Bahçe Odası',         en: 'Garden Room',
    path: '/rooms/garden-room', cat: 'room',
    kw: ['bahçe','garden','oda','room','salamis garden'] },

  { tr: 'Süit Oda',            en: 'Suite Room',
    path: '/rooms/suit-room', cat: 'room',
    kw: ['süit','suite','suit','oda','room','lüks','luxury','vip'] },

  { tr: 'Standart Villa',      en: 'Standard Villa',
    path: '/rooms/standart-villa', cat: 'room',
    kw: ['villa','standart','standard','müstakil','detached'] },

  { tr: 'Junior Villa',        en: 'Junior Villa',
    path: '/rooms/junior-villa', cat: 'room',
    kw: ['villa','junior','küçük','small'] },

  { tr: 'Duplex Villa',        en: 'Duplex Villa',
    path: '/rooms/duplex-villa', cat: 'room',
    kw: ['villa','duplex','iki katlı','two floor','iki kat'] },

  // ── Restaurants & Bars ───────────────────────────────────────────────────
  { tr: 'Çin Restoranı',       en: 'Chinese Restaurant',
    path: '/flavors/chinese-restaurant', cat: 'dining',
    kw: ['çin','chinese','china','restoran','restaurant','asya','asian','uzakdoğu','far east','wok'] },

  { tr: 'İtalyan Restoranı',   en: 'Italian Restaurant',
    path: '/flavors/italian-restaurant', cat: 'dining',
    kw: ['italyan','italian','italy','pasta','pizza','restoran','restaurant','makarna'] },

  { tr: 'Selçuk Restoran',     en: 'Selçuk Restaurant',
    path: '/flavors/selcuk-restaurant', cat: 'dining',
    kw: ['selçuk','selcuk','türk','turkish','açık büfe','buffet','restoran','restaurant','ana','main'] },

  { tr: 'Othello Bar',         en: 'Othello Bar',
    path: '/flavors/othello-bar', cat: 'dining',
    kw: ['othello','bar','içki','drink','cocktail','kokteyl','akşam','evening'] },

  { tr: 'Kokteyl Bar',         en: 'Cocktail Bar',
    path: '/flavors/coctail-bar', cat: 'dining',
    kw: ['cocktail','kokteyl','coctail','bar','içki','drink','karışık','mixed'] },

  { tr: 'Patio Cafe',          en: 'Patio Cafe',
    path: '/flavors/patio-cafe', cat: 'dining',
    kw: ['patio','cafe','kafe','kahve','coffee','hafif','snack','tatlı','atıştırmalık'] },

  { tr: 'Pier Bar',            en: 'Pier Bar',
    path: '/flavors/pier-bar', cat: 'dining',
    kw: ['pier','iskele','bar','deniz','sea','beach','plaj','rıhtım'] },

  { tr: 'Lobby Bar',           en: 'Lobby Bar',
    path: '/flavors/lobby-bar', cat: 'dining',
    kw: ['lobby','lobi','bar','içki','drink','karşılama','welcome'] },

  { tr: 'Havuz Başı Bar',      en: 'Pool Bar',
    path: '/flavors/pool-bar', cat: 'dining',
    kw: ['pool','havuz','bar','içki','drink','yüzme','swim','açık','outdoor'] },

  { tr: 'Plaj Barı',           en: 'Beach Bar',
    path: '/flavors/beach-bar', cat: 'dining',
    kw: ['beach','plaj','bar','deniz','sea','açık','outdoor'] },

  { tr: 'Saluna Pastanesi',    en: 'Saluna Patisserie',
    path: '/flavors/saluna-patisserie', cat: 'dining',
    kw: ['saluna','pastane','patisserie','pasta','tatlı','dessert','kek','cake','kahve','coffee','börek'] },

  { tr: 'Corner Pub',          en: 'Corner Pub',
    path: '/flavors/corner-pub', cat: 'dining',
    kw: ['corner','pub','bar','bira','beer','içki','drink','İngiliz','english','british'] },

  // ── Meeting Halls ────────────────────────────────────────────────────────
  { tr: 'Salamis Salloon',          en: 'Salamis Salloon',
    path: '/events/meeting-halls/salamis-salloon', cat: 'hall',
    kw: ['salamis','salon','salloon','toplantı','meeting','konferans','conference','büyük','büyük salon','balo','ballroom'] },

  { tr: 'Namık Kemal Salonu',       en: 'Namık Kemal Hall',
    path: '/events/meeting-halls/namik-kemal', cat: 'hall',
    kw: ['namık','kemal','salon','hall','toplantı','meeting','konferans'] },

  { tr: 'Othello Salonu',           en: 'Othello Hall',
    path: '/events/meeting-halls/othello', cat: 'hall',
    kw: ['othello','salon','hall','toplantı','meeting'] },

  { tr: 'Lala Mustafa Paşa Salonu', en: 'Lala Mustafa Pasha Hall',
    path: '/events/meeting-halls/lala-mustafa-pasa', cat: 'hall',
    kw: ['lala','mustafa','paşa','pasha','salon','hall','toplantı','meeting'] },

  { tr: 'Kantara Salonu',           en: 'Kantara Hall',
    path: '/events/meeting-halls/kantara', cat: 'hall',
    kw: ['kantara','salon','hall','toplantı','meeting'] },

  { tr: 'Fuaye',                    en: 'Foyer',
    path: '/events/meeting-halls/fuaye', cat: 'hall',
    kw: ['fuaye','foyer','salon','hall','lobi','lobby','koridor'] },

  { tr: 'Canbulat Salonu',          en: 'Canbulat Hall',
    path: '/events/meeting-halls/canbulat', cat: 'hall',
    kw: ['canbulat','salon','hall','toplantı','meeting'] },

  { tr: 'Akkule Salonu',            en: 'Akkule Hall',
    path: '/events/meeting-halls/akkule', cat: 'hall',
    kw: ['akkule','salon','hall','toplantı','meeting'] },

  { tr: 'Barnabas Salonu',          en: 'Barnabas Hall',
    path: '/events/meeting-halls/barnabas', cat: 'hall',
    kw: ['barnabas','salon','hall','toplantı','meeting'] },

  // ── Facilities ───────────────────────────────────────────────────────────
  { tr: 'Fitness Merkezi',     en: 'Fitness Center',
    path: '/spa', cat: 'facility',
    kw: ['fitness','spor','gym','jimnastik','egzersiz','exercise','sağlık','health','ağırlık','weight'] },

  { tr: 'Özel Plaj',          en: 'Private Beach',
    path: '/rooms', cat: 'facility',
    kw: ['plaj','beach','özel','private','deniz','sea','yüzme','swim','güneşlenme','sunbath'] },

  { tr: 'Toplantı Salonları', en: 'Meeting Halls',
    path: '/events', cat: 'facility',
    kw: ['toplantı','meeting','konferans','conference','salon','hall','etkinlik','event','düğün','wedding','kongre'] },

]

// ── Computed items (locale-aware paths + unified search string) ────────────
const allItems = computed(() => {
  const isEn   = locale.value === 'en'
  const prefix = isEn ? '/en' : ''
  return RAW.map(item => ({
    label:   isEn ? item.en : item.tr,
    path:    prefix + item.path,
    cat:     item.cat,
    _search: [item.tr, item.en, ...(item.kw ?? [])].join(' ').toLowerCase()
  }))
})

// ── Default suggestions (before typing) ──────────────────────────────────
const defaultItems = computed(() => {
  const isEn = locale.value === 'en'
  const p    = isEn ? '/en/' : '/'
  return [
    { label: isEn ? 'Rooms'              : 'Odalar',               path: `${p}rooms`,   cat: 'page' },
    { label: isEn ? 'Restaurants & Bars' : 'Restoranlar & Barlar', path: `${p}flavors`, cat: 'page' },
    { label: 'Lemon Spa & Wellness',                                path: `${p}spa`,     cat: 'page' },
    { label: isEn ? 'Events'             : 'Etkinlikler',          path: `${p}events`,  cat: 'page' },
    { label: isEn ? 'FAQ'                : 'Sık Sorulan Sorular',  path: `${p}faqs`,    cat: 'page' },
    { label: isEn ? 'Contact'            : 'İletişim',             path: `${p}contact`, cat: 'page' },
  ]
})

// ── Filtered results ──────────────────────────────────────────────────────
const hits = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return []
  return allItems.value.filter(i => i._search.includes(q)).slice(0, 16)
})

// Navigate to first result on Enter
function goFirst() {
  const first = query.value.trim() ? hits.value[0] : defaultItems.value[0]
  if (first) { navigateTo(first.path); close() }
}

// ── Category labels ───────────────────────────────────────────────────────
const CAT = {
  en: { room: 'Room', dining: 'Restaurant / Bar', hall: 'Meeting Hall', facility: 'Facility' },
  tr: { room: 'Oda',  dining: 'Restoran / Bar',   hall: 'Toplantı Salonu', facility: 'Tesis' }
}
function catLabel(cat) { return CAT[locale.value]?.[cat] ?? '' }
</script>

<style>
/* All styles are global because the modal lives outside the component DOM via Teleport */

/* ─── Overlay ─────────────────────────────────────────────── */
.ss-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(4, 35, 51, 0.72);
  backdrop-filter: blur(8px) saturate(110%);
  -webkit-backdrop-filter: blur(8px) saturate(110%);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: clamp(64px, 10vh, 130px);
  cursor: pointer;
}

/* ─── Box ─────────────────────────────────────────────────── */
.ss-box {
  width: min(660px, 92vw);
  background: rgba(4, 35, 51, 0.97);
  border: 1px solid rgba(231, 187, 127, 0.22);
  border-radius: 12px;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.55);
  overflow: hidden;
  cursor: default;
}

/* ─── Input row ───────────────────────────────────────────── */
.ss-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(231, 187, 127, 0.12);
}
.ss-icon {
  color: #E7BB7F;
  flex-shrink: 0;
}
.ss-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 18px;
  color: #FDFAF5;
  letter-spacing: 0.04em;
  min-width: 0;
}
.ss-input::placeholder {
  color: rgba(253, 250, 245, 0.3);
}
/* hide native search clear button */
.ss-input[type="search"]::-webkit-search-cancel-button { display: none; }

.ss-close {
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(253, 250, 245, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  transition: color 150ms, background 150ms;
  flex-shrink: 0;
}
.ss-close:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

/* ─── Results ─────────────────────────────────────────────── */
.ss-results {
  max-height: min(420px, 55vh);
  overflow-y: auto;
  padding: 10px 0 8px;
  overscroll-behavior: contain;
}
.ss-results::-webkit-scrollbar { width: 4px; }
.ss-results::-webkit-scrollbar-track { background: transparent; }
.ss-results::-webkit-scrollbar-thumb { background: rgba(231, 187, 127, 0.2); border-radius: 4px; }

.ss-label {
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(253, 250, 245, 0.3);
  padding: 4px 20px 6px;
  margin: 0;
}

.ss-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  gap: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: background 100ms;
}
.ss-result:hover {
  background: rgba(231, 187, 127, 0.09);
}
.ss-rlabel {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 16px;
  color: rgba(253, 250, 245, 0.88);
  letter-spacing: 0.03em;
  line-height: 1.3;
}
.ss-rcat {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #E7BB7F;
  border: 1px solid rgba(231, 187, 127, 0.28);
  border-radius: 3px;
  padding: 2px 7px;
  white-space: nowrap;
  flex-shrink: 0;
}

.ss-empty {
  padding: 24px 20px;
  margin: 0;
  text-align: center;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 15px;
  color: rgba(253, 250, 245, 0.35);
}

/* ─── Transition ──────────────────────────────────────────── */
.ss-fade-enter-active,
.ss-fade-leave-active { transition: opacity 180ms ease; }
.ss-fade-enter-from,
.ss-fade-leave-to { opacity: 0; }
</style>
